/**
 * Toast Notifications Store Module
 *
 * Provides centralized toast notification management with duplicate prevention.
 * Prevents the same message from showing multiple times within a 3-second window.
 */

import Vue from 'vue';

const DUPLICATE_TIMEOUT = 3000; // 3 seconds

export const TOAST = {
  namespaced: true,

  state: {
    // Track recent messages with timestamps to prevent duplicates
    recentMessages: new Map(),
  },

  getters: {
    /**
     * Check if a message was recently shown
     */
    isRecentMessage: (state) => (message) => {
      const lastShown = state.recentMessages.get(message);
      if (!lastShown) return false;
      return Date.now() - lastShown < DUPLICATE_TIMEOUT;
    },
  },

  mutations: {
    /**
     * Record a message as recently shown
     */
    RECORD_MESSAGE(state, message) {
      state.recentMessages.set(message, Date.now());
    },

    /**
     * Clean up old messages from the tracking map
     */
    CLEANUP_OLD_MESSAGES(state) {
      const now = Date.now();
      for (const [message, timestamp] of state.recentMessages.entries()) {
        if (now - timestamp >= DUPLICATE_TIMEOUT) {
          state.recentMessages.delete(message);
        }
      }
    },
  },

  actions: {
    /**
     * Show an error toast notification
     * @param {string} message - The message to display
     * @param {Object} options - Optional configuration (position, timeout)
     */
    ERROR({ commit, getters }, { message, options = {} }) {
      if (getters.isRecentMessage(message)) return;

      commit('RECORD_MESSAGE', message);
      commit('CLEANUP_OLD_MESSAGES');

      const defaultOptions = {
        position: 'top-right',
        timeout: 2000,
        ...options,
      };

      Vue.prototype.$dialog?.notify?.error(message, defaultOptions);
    },

    /**
     * Show a success toast notification
     * @param {string} message - The message to display
     * @param {Object} options - Optional configuration (position, timeout)
     */
    SUCCESS({ commit, getters }, { message, options = {} }) {
      if (getters.isRecentMessage(message)) return;

      commit('RECORD_MESSAGE', message);
      commit('CLEANUP_OLD_MESSAGES');

      const defaultOptions = {
        position: 'top-right',
        timeout: 2000,
        ...options,
      };

      Vue.prototype.$dialog?.notify?.success(message, defaultOptions);
    },

    /**
     * Show a warning toast notification
     * @param {string} message - The message to display
     * @param {Object} options - Optional configuration (position, timeout)
     */
    WARNING({ commit, getters }, { message, options = {} }) {
      if (getters.isRecentMessage(message)) return;

      commit('RECORD_MESSAGE', message);
      commit('CLEANUP_OLD_MESSAGES');

      const defaultOptions = {
        position: 'top-right',
        timeout: 2000,
        ...options,
      };

      Vue.prototype.$dialog?.notify?.warning(message, defaultOptions);
    },

    /**
     * Show an info toast notification
     * @param {string} message - The message to display
     * @param {Object} options - Optional configuration (position, timeout)
     */
    INFO({ commit, getters }, { message, options = {} }) {
      if (getters.isRecentMessage(message)) return;

      commit('RECORD_MESSAGE', message);
      commit('CLEANUP_OLD_MESSAGES');

      const defaultOptions = {
        position: 'top-right',
        timeout: 2000,
        ...options,
      };

      Vue.prototype.$dialog?.notify?.info(message, defaultOptions);
    },
  },
};
